import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { ProtectedRoute } from '@/lib/auth/ProtectedRoute';
import { useAuth } from '@/lib/auth/useAuth';
import { AppShell } from '@/components/layout/AppShell';
import { CircleProvider } from '@/lib/circle/CircleContext';
import { RequireOnboarded } from '@/lib/circle/RequireOnboarded';

// Route-level code splitting. The initial bundle now only contains the
// shell + landing/auth pages; every authenticated screen is its own
// chunk loaded on demand. Doc 3 budget is <350KB initial JS.
const LandingPage = lazy(() => import('@/pages/LandingPage').then((m) => ({ default: m.LandingPage })));
const SignInPage = lazy(() => import('@/pages/SignInPage').then((m) => ({ default: m.SignInPage })));
const SignUpPage = lazy(() => import('@/pages/SignUpPage').then((m) => ({ default: m.SignUpPage })));
const OnboardingPage = lazy(() =>
  import('@/pages/OnboardingPage').then((m) => ({ default: m.OnboardingPage })),
);
const InviteAcceptPage = lazy(() =>
  import('@/pages/InviteAcceptPage').then((m) => ({ default: m.InviteAcceptPage })),
);
const DashboardPage = lazy(() =>
  import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const TasksPage = lazy(() => import('@/pages/TasksPage').then((m) => ({ default: m.TasksPage })));
const CalendarPage = lazy(() =>
  import('@/pages/CalendarPage').then((m) => ({ default: m.CalendarPage })),
);
const FeedPage = lazy(() => import('@/pages/FeedPage').then((m) => ({ default: m.FeedPage })));
const MessagesPage = lazy(() =>
  import('@/pages/MessagesPage').then((m) => ({ default: m.MessagesPage })),
);
const DocumentsPage = lazy(() =>
  import('@/pages/DocumentsPage').then((m) => ({ default: m.DocumentsPage })),
);
const SagePage = lazy(() => import('@/pages/SagePage').then((m) => ({ default: m.SagePage })));
const FamilyPage = lazy(() =>
  import('@/pages/FamilyPage').then((m) => ({ default: m.FamilyPage })),
);
const CareRecipientPage = lazy(() =>
  import('@/pages/CareRecipientPage').then((m) => ({ default: m.CareRecipientPage })),
);
const MedicationsPage = lazy(() =>
  import('@/pages/MedicationsPage').then((m) => ({ default: m.MedicationsPage })),
);
const SettingsPage = lazy(() =>
  import('@/pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const LegalDocumentPage = lazy(() =>
  import('@/pages/LegalDocumentPage').then((m) => ({ default: m.LegalDocumentPage })),
);

function PageFallback(): JSX.Element {
  return (
    <div className="flex min-h-[60vh] items-center justify-center text-sm text-muted-foreground">
      Loading…
    </div>
  );
}

// The "/" route. A signed-in user (including one just bounced back from Google
// OAuth with a ?code= to exchange) goes straight into the app instead of seeing
// the marketing landing page.
function Home(): JSX.Element {
  const { session, loading } = useAuth();
  // Hold the spinner while the initial session resolves, or while an OAuth code
  // in the URL is still being exchanged — otherwise the landing page flashes.
  const exchangingCode = new URLSearchParams(window.location.search).has('code');
  if (loading || (exchangingCode && !session)) return <PageFallback />;
  return session ? <Navigate to="/app" replace /> : <LandingPage />;
}

export function App(): JSX.Element {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/signin" element={<SignInPage />} />
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/legal/terms" element={<LegalDocumentPage type="terms" />} />
        <Route path="/legal/privacy" element={<LegalDocumentPage type="privacy" />} />
        {/* Short aliases so /terms and /privacy resolve too — the App Store paywall
            and any external link may use either form; App Review requires them to
            be functional (not fall through to the marketing page). */}
        <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
        <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
        <Route
          path="/legal/notice-of-privacy-practices"
          element={<LegalDocumentPage type="npp" />}
        />
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />
        {/* Public on purpose: the invite landing page introduces the circle
            to signed-out users and routes them through signup/signin, then
            redeems once they're authenticated. */}
        <Route path="/invite/:token" element={<InviteAcceptPage />} />
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <CircleProvider>
                <RequireOnboarded>
                  <AppShell />
                </RequireOnboarded>
              </CircleProvider>
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="tasks" element={<TasksPage />} />
          <Route path="calendar" element={<CalendarPage />} />
          <Route path="feed" element={<FeedPage />} />
          <Route path="messages" element={<MessagesPage />} />
          <Route path="documents" element={<DocumentsPage />} />
          <Route path="sage" element={<SagePage />} />
          <Route path="family" element={<FamilyPage />} />
          <Route path="care-recipient" element={<CareRecipientPage />} />
          <Route path="medications" element={<MedicationsPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
